import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWizardUploadInstructions } from 'src/components/DUP/molecules/WizardUpload/useWizardUploadInstructions';
import {
  useRequiredDocsModal,
  UseRequiredDocsModalProps
} from 'src/pages/DUPPage/useRequiredDocsModal';
import { UnauthenticateSessionProofType } from 'src/types/api';

type WizardStepNumber = 1 | 2 | 3 | 4;

export type StepsProps = ReturnType<typeof useWizardSteps>;

export interface StepInfoAttributes {
  'data-step-number': string;
  'data-step-title': string;
}

export interface WizardStepInfo {
  title: string;
  description: string;
  stepNumber: WizardStepNumber;
  stepInfoAttributes: StepInfoAttributes;
}

const DUP_STEP_KEY_PREFIX = 'dup.wizard.step';

export function useWizardSteps({
  connectedPayrollEnabled,
  property,
  ...rest
}: {
  connectedPayrollEnabled: boolean;
} & UseRequiredDocsModalProps) {
  const { t } = useTranslation();

  const { instructions } = useWizardUploadInstructions({
    // These instructions are for step 3, so it should match the step 3 proof type
    proofType: UnauthenticateSessionProofType.BankStatement,
    property
  });

  const [activeStepIdx, setActiveStepIdx] = useState(0);

  const { onNext, RequiredDocsModal } = useRequiredDocsModal({ property, ...rest });

  const handleNext = ({
    hasPayrollAccountsConnected,
    hasBankAccountsConnected
  }: {
    hasPayrollAccountsConnected: boolean;
    hasBankAccountsConnected: boolean;
  }) => {
    const shouldContinue = onNext({
      stepIdx: activeStepIdx,
      hasPayrollAccountsConnected,
      hasBankAccountsConnected
    });
    if (shouldContinue) setActiveStepIdx((prevStep) => prevStep + 1);
  };

  const info: WizardStepInfo[] = ([1, 2, 3, 4] as WizardStepNumber[]).map((stepNumber) => {
    let description = t(`${DUP_STEP_KEY_PREFIX}.${stepNumber}.description`);
    if (stepNumber === 2 && connectedPayrollEnabled) {
      description = t(`${DUP_STEP_KEY_PREFIX}.2.descriptionConnectedPayroll`);
    } else if (stepNumber === 3) {
      description = instructions[0] + description;
    }

    return {
      stepNumber,
      title: t(`${DUP_STEP_KEY_PREFIX}.${stepNumber}.title`),
      description,
      stepInfoAttributes: {
        'data-step-number': `${stepNumber}`,
        'data-step-title': t(`${DUP_STEP_KEY_PREFIX}.${stepNumber}.title`, { lng: 'en' })
      }
    };
  });

  return {
    info,
    activeStepIdx,
    setActiveStepIdx,
    onFinished: handleNext,
    goBack: () => setActiveStepIdx((prevStep) => prevStep - 1),
    RequiredDocsModal
  };
}
